import React, { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_URL + '/api';

export default function ExceptionsModal({
  isOpen,
  onClose,
  assessmentId,
  domainId,
  departmentId
}) {
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ action: 'CAP', rationale: '', due_at: '' });

  const sessionUser = (() => { try { return JSON.parse(localStorage.getItem('sessionUser') || 'null'); } catch { return null; } })();
  const role = sessionUser?.app_roles?.risk?.role;
  const canApprove = role === 'officer' || sessionUser?.role === 'superadmin';

  useEffect(() => {
    if (!isOpen || !assessmentId) return;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE}/risk-assessment/assessments/${assessmentId}/exceptions`, { credentials: 'include' });
        const json = await res.json();
        if (json.success) setList(json.exceptions || []);
      } catch {}
      setLoading(false);
    })();
  }, [isOpen, assessmentId]);

  const submit = async () => {
    if (!form.rationale.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/risk-assessment/assessments/${assessmentId}/exceptions`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          domain_id: domainId,
          department_id: departmentId || null,
          rationale: form.rationale,
          action: form.action,
          due_at: form.due_at || null
        })
      });
      const json = await res.json();
      if (json.success) {
        setList(prev => [json.exception, ...prev]);
        setForm({ action: 'CAP', rationale: '', due_at: '' });
      }
    } catch {}
    setLoading(false);
  };

  const approve = async (eid, approve = true) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/risk-assessment/assessments/${assessmentId}/exceptions/${eid}/approve`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ approve })
      });
      const json = await res.json();
      if (json.success) {
        setList(prev => prev.map(x => x.id === eid ? json.exception : x));
      }
    } catch {}
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-slate-900/70" onClick={onClose} />
      <div className="relative bg-white w-full max-w-3xl rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">Exceptions</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">✕</button>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-auto">
          <div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <label className="text-sm">Action
                <select value={form.action} onChange={(e)=>setForm({...form, action: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg">
                  <option value="CAP">CAP</option>
                  <option value="MONITOR">MONITOR</option>
                  <option value="ACCEPT">ACCEPT</option>
                </select>
              </label>
              <label className="text-sm">Due date
                <input type="date" value={form.due_at} onChange={(e)=>setForm({...form, due_at: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg" />
              </label>
            </div>
            <label className="text-sm block mt-3">Rationale
              <textarea value={form.rationale} onChange={(e)=>setForm({...form, rationale: e.target.value})} rows={3} className="w-full mt-1 px-3 py-2 border rounded-lg" placeholder="Why this risk breaches appetite and what you propose" />
            </label>
            <div className="mt-3 text-right">
              <button disabled={loading || !form.rationale.trim()} onClick={submit} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50">Flag Exception</button>
            </div>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <h4 className="font-medium text-slate-900 mb-2">Existing Exceptions</h4>
            {list.length === 0 ? (
              <div className="text-sm text-slate-500">No exceptions yet.</div>
            ) : (
              <div className="space-y-2">
                {list.map(ex => (
                  <div key={ex.id} className="p-3 border border-slate-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-slate-700">{ex.domain_name} {ex.department_name ? `• ${ex.department_name}` : ''}</div>
                      <div className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{ex.status}</div>
                    </div>
                    <div className="text-sm text-slate-600 mt-1">{ex.rationale}</div>
                    <div className="flex items-center justify-between mt-2 text-sm">
                      <div className="text-slate-500">Action: {ex.action}{ex.due_at ? ` • Due ${new Date(ex.due_at).toLocaleDateString()}` : ''}</div>
                      {canApprove && ex.status === 'pending' && (
                        <div className="space-x-2">
                          <button onClick={()=>approve(ex.id, true)} className="px-3 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded">Approve</button>
                          <button onClick={()=>approve(ex.id, false)} className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded">Reject</button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


